import React, { useMemo, useState } from "react";
import { requiresMandatoryLOs } from "../../utils/completionCriteria";
import { Box, Typography, useTheme } from "@mui/material";
import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import BannerCst from "@components/BannerCst";
import SelectedLOList from "@components/SelectedLOList";
import { useWizardReadOnly } from "@components/WizardCst";
import { WizardStepProps } from "@components/WizardCst/types";
import LOAlternativesPickerModal from "../../components/LOAlternativesPickerModal";
import ContentActionsBarCst from "../../components/ContentActionsBarCst";
import AddLOToPathModal, {
  AddLOSelectedItem,
} from "../components/AddLOToPathModal";
import CreateActivityModalCst, {
  CreatedDraftLo,
} from "../../components/CreateActivityModalCst";
import CreateCourseModalCst from "../../components/CreateCourseModalCst";
import CreateContentChooserModal, {
  CreateContentKind,
} from "../components/CreateContentChooserModal";
import {
  AlternativeLOSection,
  LOCompletionCriteria,
  SelectedLOItem,
} from "../../types";
import {
  flattenSelectedLOItems,
  removeLOFromSection,
} from "../../utils/loStructure";
import { PathWizardData } from "../types";
import { ButtonPrimary } from "src/UI/ButtonPrimary";
import { ButtonSecondary } from "src/UI/ButtonSecondary";

interface AddPathContentStepProps extends WizardStepProps<PathWizardData> {
  lockAdditions?: boolean;
  lockReorder?: boolean;
}

const AddPathContentStep: React.FC<AddPathContentStepProps> = ({
  data,
  setData,
  lockAdditions = false,
  lockReorder = false,
}) => {
  const { t } = useTranslation("wizard-path");
  const { t: tCatalog } = useTranslation("catalog-v2");
  const theme = useTheme();
  const readOnly = useWizardReadOnly();
  const additionsDisabled = lockAdditions || readOnly;
  const completionCriteriaEnabled = requiresMandatoryLOs(
    data.completion_criterion,
  );

  const [contentPickerOpen, setContentPickerOpen] = useState(false);
  const [alternativeSectionIndex, setAlternativeSectionIndex] = useState<
    number | "new" | null
  >(null);
  const [chooserOpen, setChooserOpen] = useState(false);
  const [createActivityOpen, setCreateActivityOpen] = useState(false);
  const [createCourseOpen, setCreateCourseOpen] = useState(false);

  const items = data.selected_los;
  const selectedLOs = useMemo(() => flattenSelectedLOItems(items), [items]);
  const count = selectedLOs.length;
  const isEmpty = count === 0;
  // Il banner è rilevante solo se nel Percorso sono stati creati (bozze)
  // Attività o Corsi, non se sono stati solo aggiunti elementi esistenti.
  const hasCreatedContent = selectedLOs.some((item) => item.entity?.is_draft);
  const activitiesCount = selectedLOs.filter(
    (item) => item.kind === "activity",
  ).length;
  const coursesCount = selectedLOs.filter(
    (item) => item.kind === "course",
  ).length;
  const counters = [
    activitiesCount > 0 &&
      t("step-content.counter-activities", { count: activitiesCount }),
    coursesCount > 0 &&
      t("step-content.counter-courses", { count: coursesCount }),
  ].filter((label): label is string => typeof label === "string");

  const excludeIds = useMemo(
    () => selectedLOs.map((item) => item.id),
    [selectedLOs],
  );

  const handlePickerConfirm = (added: AddLOSelectedItem[]) => {
    if (added.length === 0) return;
    const known = new Set(excludeIds);
    const fresh: SelectedLOItem[] = added
      .filter((a) => !known.has(a.id))
      .map((a) => ({
        id: a.id,
        title: a.title,
        type: a.type,
        kind: a.kind,
        entity: a.entity,
        completion_criteria: null,
      }));
    if (fresh.length === 0) return;

    setData({ selected_los: [...items, ...fresh] });
  };

  const alternativeSection = useMemo(() => {
    if (typeof alternativeSectionIndex !== "number") return null;
    const item = items[alternativeSectionIndex];
    return item?.kind === "section" ? item : null;
  }, [alternativeSectionIndex, items]);

  const alternativeExcludeIds = useMemo(() => {
    if (typeof alternativeSectionIndex !== "number") return excludeIds;
    return flattenSelectedLOItems(
      items.filter((_, index) => index !== alternativeSectionIndex),
    ).map((item) => item.id);
  }, [alternativeSectionIndex, excludeIds, items]);

  const handleAlternativeConfirm = (section: AlternativeLOSection) => {
    if (!completionCriteriaEnabled) return;
    if (alternativeSectionIndex === "new") {
      setData({ selected_los: [...items, section] });
    } else if (typeof alternativeSectionIndex === "number") {
      if (items[alternativeSectionIndex]?.locked) return;
      setData({
        selected_los: items.map((item, index) =>
          index === alternativeSectionIndex ? section : item,
        ),
      });
    }
    setAlternativeSectionIndex(null);
  };

  const handleRemoveSection = (sectionIndex: number) => {
    if (items[sectionIndex]?.locked) return;
    setData({
      selected_los: items.filter((_, index) => index !== sectionIndex),
    });
  };

  const handleRemove = (id: number) => {
    setData({
      selected_los: items.filter(
        (item) => item.kind === "section" || item.id !== id,
      ),
    });
  };

  const handleRemoveFromSection = (sectionIndex: number, id: number) => {
    if (items[sectionIndex]?.locked) return;
    setData({
      selected_los: removeLOFromSection(items, sectionIndex, id),
    });
  };

  const handleReorder = (fromIndex: number, toIndex: number) => {
    const next = [...items];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    setData({ selected_los: next });
  };

  const handleCreated = (item: CreatedDraftLo, kind: CreateContentKind) => {
    if (excludeIds.includes(item.id)) return;
    const fresh: SelectedLOItem = {
      id: item.id,
      title: item.title,
      type: item.type,
      kind,
      entity: item.entity,
      completion_criteria: null,
    };
    setData({ selected_los: [...items, fresh] });
  };

  const handleChoose = (kind: CreateContentKind) => {
    setChooserOpen(false);
    if (kind === "activity") setCreateActivityOpen(true);
    else setCreateCourseOpen(true);
  };

  // TODO: pagina dettaglio v2 non disponibile oggi.
  const handleDetails = (item: SelectedLOItem) => {
    console.info("details — TODO", item.kind, item.id);
  };

  const handleCompletionCriteriaChange = (
    item: SelectedLOItem,
    value: LOCompletionCriteria,
  ) => {
    if (!completionCriteriaEnabled) return;
    setData({
      selected_los: items.map((current) =>
        current.kind !== "section" &&
        current.id === item.id &&
        current.kind === item.kind
          ? { ...current, completion_criteria: value }
          : current,
      ),
    });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      {hasCreatedContent && (
        <BannerCst type="warning" label={t("step-content.publish-banner")} />
      )}

      {isEmpty && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            padding: "12px 16px",
            borderRadius: "12px",
            backgroundColor: theme.palette.warning.light,
            border: `1px solid ${theme.palette.warning.main}`,
          }}
        >
          <AlertTriangle size={20} color={theme.palette.warning.dark} />
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 500,
              color: theme.palette.common.black,
            }}
          >
            {t("step-content.empty-warning")}
          </Typography>
        </Box>
      )}

      <ContentActionsBarCst isEmpty={isEmpty} counters={counters}>
        <ButtonPrimary
          size="md"
          onClick={() => setContentPickerOpen(true)}
          disabled={additionsDisabled}
        >
          {t("step-content.add")}
        </ButtonPrimary>
        {completionCriteriaEnabled && (
          <ButtonSecondary
            size="md"
            onClick={() => setAlternativeSectionIndex("new")}
            disabled={additionsDisabled}
          >
            {t("step-content.add-alternative")}
          </ButtonSecondary>
        )}
        <ButtonSecondary
          size="md"
          onClick={() => setChooserOpen(true)}
          disabled={additionsDisabled}
        >
          {t("step-content.create-new")}
        </ButtonSecondary>
      </ContentActionsBarCst>

      {!isEmpty && (
        <SelectedLOList
          items={items}
          tCatalog={tCatalog}
          removeLabel={t("step-content.remove")}
          onRemove={handleRemove}
          onRemoveFromSection={handleRemoveFromSection}
          onEditSection={lockAdditions ? undefined : setAlternativeSectionIndex}
          onRemoveSection={lockAdditions ? undefined : handleRemoveSection}
          onDetails={handleDetails}
          onReorder={handleReorder}
          reorderDisabled={lockReorder}
          completionCriteriaEnabled={completionCriteriaEnabled}
          onCompletionCriteriaChange={handleCompletionCriteriaChange}
        />
      )}

      <AddLOToPathModal
        open={contentPickerOpen}
        onClose={() => setContentPickerOpen(false)}
        excludeIds={excludeIds}
        onConfirm={handlePickerConfirm}
      />

      <LOAlternativesPickerModal
        open={completionCriteriaEnabled && alternativeSectionIndex !== null}
        mode="path"
        section={alternativeSection}
        excludeIds={alternativeExcludeIds}
        onClose={() => setAlternativeSectionIndex(null)}
        onConfirm={handleAlternativeConfirm}
      />

      <CreateContentChooserModal
        open={chooserOpen}
        onClose={() => setChooserOpen(false)}
        onChoose={handleChoose}
      />

      <CreateActivityModalCst
        open={createActivityOpen}
        onClose={() => setCreateActivityOpen(false)}
        onCreated={(item) => handleCreated(item, "activity")}
      />

      <CreateCourseModalCst
        open={createCourseOpen}
        onClose={() => setCreateCourseOpen(false)}
        onCreated={(item) => handleCreated(item, "course")}
      />
    </Box>
  );
};

export default AddPathContentStep;
